// @ts-check
// @babel/preset-env will transform this into individual core-js modules based on the target
import 'core-js/stable';
// https://github.com/tc39/proposal-joint-iteration
import 'core-js/proposals/joint-iteration';

if (!Object.hasOwn(Promise, 'try')) {
  Object.defineProperty(Promise, 'try', {
    writable: true,
    enumerable: true,
    configurable: true,
    /** @type {typeof Promise.try} */
    value(fn, ...args) {
      try {
        return this.resolve(fn(...args));
      } catch (error) {
        return this.reject(error);
      }
    },
  });
}

if (!Object.hasOwn(AbortSignal, 'timeout')) {
  /**
   * @param {AbortController} abortController
   */
  const handleTimeout = abortController => {
    abortController.abort(new DOMException('signal timed out', 'TimeoutError'));
  };

  Object.defineProperty(AbortSignal, 'timeout', {
    writable: true,
    enumerable: true,
    configurable: true,
    /** @type {typeof AbortSignal.timeout} */
    value: function timeout(timeoutMs) {
      const abortController = new AbortController();
      setTimeout(handleTimeout, timeoutMs, abortController);
      return abortController.signal;
    },
  });
}

// https://developer.mozilla.org/en-US/docs/Web/API/AbortSignal/any_static
if (!Object.hasOwn(AbortSignal, 'any')) {
  const signalControllers = new WeakMap();

  const signalFinalizer = new FinalizationRegistry(
    ([signalRef, callbackRef]) => {
      try {
        const signal = signalRef.deref();
        if (signal !== undefined) {
          const callback = callbackRef.deref();
          if (callback !== undefined) {
            signal.removeEventListener('abort', callback);
          }
        }
      } catch {
        // do nothing
      }
    },
  );

  Object.defineProperty(AbortSignal, 'any', {
    writable: true,
    enumerable: true,
    configurable: true,
    /** @type {typeof AbortSignal.any} */
    value: function any(signals) {
      // return early if some signal is already aborted
      for (const signal of signals) {
        if (signal.aborted) {
          return signal;
        }
      }

      const abortController = new AbortController();

      // Force the signal to hold strongly onto its controller
      signalControllers.set(abortController.signal, abortController);

      const abortControllerRef = new WeakRef(abortController);

      /** @this {AbortSignal} */
      function handleAbort() {
        // Parent signal must not prevent child signals from being reclaimed
        abortControllerRef.deref()?.abort(this.reason);
      }

      for (const signal of signals) {
        signal.addEventListener('abort', handleAbort);
        signalFinalizer.register(
          abortController.signal,
          // Child signals must not prevent parent signals from being reclaimed
          [new WeakRef(signal), new WeakRef(handleAbort)],
        );
      }

      return abortController.signal;
    },
  });
}

// https://developer.mozilla.org/en-US/docs/Web/API/AbortSignal/reason
if (!Object.hasOwn(AbortSignal.prototype, 'reason')) {
  const abortReasons = new WeakMap();
  const abortControllerAbort = AbortController.prototype.abort;

  Object.defineProperty(AbortController.prototype, 'abort', {
    enumerable: true,
    configurable: true,
    /**
     * @this {AbortController}
     * @type {typeof AbortController.prototype.abort}
     */
    value: function abort(
      // Chromium using this phrasing
      // https://chromium.googlesource.com/chromium/src/+/1e919298/third_party/blink/renderer/core/dom/abort_signal.cc#104
      reason = new DOMException(
        'signal is aborted without reason',
        'AbortError',
      ),
    ) {
      abortReasons.set(this.signal, reason);
      return abortControllerAbort.call(this);
    },
  });

  Object.defineProperty(AbortSignal.prototype, 'reason', {
    enumerable: true,
    configurable: true,
    /**
     * @this {AbortSignal}
     * @type {ReturnType<typeof AbortSignal.prototype.reason>}
     */
    get() {
      return abortReasons.get(this);
    },
  });
}

if (!Object.hasOwn(AbortSignal.prototype, 'throwIfAborted')) {
  Object.defineProperty(AbortSignal.prototype, 'throwIfAborted', {
    writable: true,
    enumerable: false,
    configurable: true,
    value: function throwIfAborted() {
      if (this.aborted) {
        throw this.reason;
      }
    },
  });
}

if (!Object.hasOwn(Blob.prototype, 'arrayBuffer')) {
  Object.defineProperty(Blob.prototype, 'arrayBuffer', {
    writable: true,
    enumerable: true,
    configurable: true,
    /**
     * @this {Blob}
     * @type {typeof Blob.prototype.arrayBuffer}
     */
    value: function arrayBuffer() {
      return new Response(this).arrayBuffer();
    },
  });
}

if (!Object.hasOwn(Blob.prototype, 'stream')) {
  Object.defineProperty(Blob.prototype, 'stream', {
    writable: true,
    enumerable: true,
    configurable: true,
    /**
     * @this {Blob}
     * @type {typeof Blob.prototype.stream}
     */
    value: function stream() {
      const response = new Response(this);
      if (response.body === null) {
        throw new Error('Response body is null');
      }
      return response.body;
    },
  });
}

if (!Object.hasOwn(Blob.prototype, 'text')) {
  Object.defineProperty(Blob.prototype, 'text', {
    writable: true,
    enumerable: true,
    configurable: true,
    /**
     * @this {Blob}
     * @type {typeof Blob.prototype.text}
     */
    value: function text() {
      return new Response(this).text();
    },
  });
}

if (
  globalThis.requestIdleCallback === undefined ||
  globalThis.cancelIdleCallback === undefined
) {
  /**
   * @param {IdleRequestCallback} callback
   * @param {IdleRequestOptions|undefined} options
   * @param {number} initialTime
   */
  const handleTimeout = (callback, options, initialTime) => {
    /** @type {IdleDeadline} */
    callback({
      didTimeout: initialTime + (options?.timeout ?? 0) <= performance.now(),
      timeRemaining() {
        const elapsedTime = performance.now() - initialTime;
        return Math.max(0, 50 - elapsedTime);
      },
    });
  };

  if (!Object.hasOwn(globalThis, 'requestIdleCallback')) {
    Object.defineProperty(globalThis, 'requestIdleCallback', {
      writable: true,
      enumerable: true,
      configurable: true,
      /** @type {typeof globalThis.requestIdleCallback} */
      value: function requestIdleCallback(callback, options) {
        /** @type {number} */
        // @ts-expect-error - setTimeout returns an object in Node.js, but requestIdleCallback expects a number
        const timeout = setTimeout(
          handleTimeout,
          0,
          callback,
          options,
          performance.now(),
        );

        return timeout;
      },
    });

    Object.defineProperty(globalThis, 'cancelIdleCallback', {
      writable: true,
      enumerable: true,
      configurable: true,
      /** @type {typeof globalThis.cancelIdleCallback} */
      value: function cancelIdleCallback(handle) {
        clearTimeout(handle);
      },
    });
  }
}
